/**
 * Composable for sanitizing and formatting HTML text content
 * with inline styles derived from slide text_style properties.
 */
export function useFormattedText() {
  const ALLOWED_TAGS = ["p", "strong", "b", "em", "i", "ul", "ol", "li", "br"];

  /**
   * Sanitize HTML content - keep only safe tags, unwrap disallowed ones.
   */
  function sanitizeHtml(html) {
    if (!html) return "";

    const temp = document.createElement("div");
    temp.innerHTML = html;

    function cleanNode(node) {
      const children = Array.from(node.childNodes);
      children.forEach((child) => {
        if (child.nodeType === Node.ELEMENT_NODE) {
          const tagName = child.tagName.toLowerCase();
          if (!ALLOWED_TAGS.includes(tagName)) {
            while (child.firstChild) {
              node.insertBefore(child.firstChild, child);
            }
            node.removeChild(child);
          } else {
            cleanNode(child);
          }
        }
      });
    }

    cleanNode(temp);
    return temp.innerHTML;
  }

  /**
   * Build a Vue style object from a text_style object. Values are never
   * interpolated into an HTML string — they are bound through `:style`, so Vue
   * escapes them and an untrusted font/color (e.g. from a report theme) cannot
   * break out of the attribute.
   */
  function buildStyleObject(textStyle = {}) {
    // Font size - use px instead of pt for correct scaling in preview
    const style = { fontSize: `${textStyle.font_size_pt || 14}px` };

    if (textStyle.bold) style.fontWeight = "bold";
    if (textStyle.italic) style.fontStyle = "italic";
    if (textStyle.font_family) style.fontFamily = `'${textStyle.font_family}', sans-serif`;
    style.color = textStyle.font_color || "#1f2937";

    return style;
  }

  /**
   * Get sanitized rich-text HTML, with <p> replaced by <div>. The result is
   * meant to be rendered via v-html; styling is applied separately through
   * buildStyleObject so no untrusted value ever reaches the HTML string.
   */
  function getFormattedTextContent(rawText) {
    const sanitized = sanitizeHtml(rawText);
    if (!sanitized) return "";

    // Replace <p> tags with <div> to avoid browser default font-size on paragraphs
    return sanitized.replace(/<p>/gi, "<div>").replace(/<\/p>/gi, "</div>");
  }

  /**
   * Adapt a report theme text style (format: { font, size, bold, color })
   * to the format expected by buildInlineStyle.
   */
  function adaptReportThemeStyle(ctStyle) {
    if (!ctStyle) return {};
    return {
      font_size_pt: ctStyle.size,
      bold: ctStyle.bold,
      italic: ctStyle.italic,
      font_family: ctStyle.font,
      font_color: ctStyle.color,
    };
  }

  return { sanitizeHtml, buildStyleObject, adaptReportThemeStyle, getFormattedTextContent };
}
